/**
 * Compra por cliente — o que a aba manual "compra por cliente" fazia, agora
 * automático a partir dos pedidos: ranking por valor e, por cliente, o que comprou
 * (quantidade por produto), quanto por ano e a lista de pedidos.
 */
import { createMemo, createResource, createSignal, For, Show } from 'solid-js'
import { Input } from '@ui/components/Input'
import { Resize } from '@core/component/Resize'
import { isMobileWidth } from '@core/mobile/mobileWidth'
import type { AnoPedidos, StatusPedido } from '@shared/types'
import { api, versaoDados } from '../../nucleo/cliente'
import { BarrasHorizontais, Colunas, type Barra } from './Graficos'
import { corDoStatus, dataBR, inteiro, moeda, moedaCurta, numero } from './formato'

export function CompraPorCliente(props: { anos: AnoPedidos[]; status: StatusPedido[] | undefined }) {
  const [ano, setAno] = createSignal<number | null>(null)
  const [busca, setBusca] = createSignal('')
  const [sel, setSel] = createSignal<number | null>(null)

  const [ranking] = createResource(
    () => ({ ano: ano(), busca: busca(), v: versaoDados() }),
    ({ ano, busca }) => api.pedidos.comprasPorCliente({ ano, busca: busca || null }),
  )
  const lista = () => ranking.latest ?? []
  const selecionado = () => sel() ?? lista()[0]?.clienteId ?? null

  const barras = createMemo<Barra[]>(() =>
    lista().map((c) => ({
      rotulo: c.cliente,
      valor: c.totalValor,
      texto: moedaCurta(c.totalValor),
      dica: `${c.cliente}: ${moeda(c.totalValor)} em ${c.pedidos} pedido(s)${c.ultimoPedido ? ` · último ${dataBR(c.ultimoPedido)}` : ''}`,
    })),
  )

  const filtros = (
    <div class="flex flex-wrap items-center gap-2">
      <select
        class="h-8 rounded-md border border-edge bg-input px-2 text-sm text-ink"
        aria-label="Ano"
        value={ano() ?? ''}
        onChange={(e) => (setAno(e.currentTarget.value ? Number(e.currentTarget.value) : null), setSel(null))}
      >
        <option value="">Todos os anos</option>
        <For each={props.anos}>{(a) => <option value={a.ano}>{a.ano}</option>}</For>
      </select>
      <Input class="w-56 max-w-full" placeholder="Buscar cliente" value={busca()} onInput={(e) => (setBusca(e.currentTarget.value), setSel(null))} />
      <span class="text-xs text-ink-subtle">{lista().length} cliente(s)</span>
    </div>
  )

  const rankingEl = (
    <div class="h-full overflow-auto p-4 flex flex-col gap-4">
      {filtros}
      <BarrasHorizontais
        titulo={`Valor comprado ${ano() ?? '(todos os anos)'}`}
        barras={barras()}
        vazio={ranking.loading ? 'Carregando…' : 'Nenhum pedido com cliente.'}
        ativo={lista().findIndex((c) => c.clienteId === selecionado())}
        aoClicar={(i) => setSel(lista()[i].clienteId)}
      />
    </div>
  )

  return (
    <Show
      when={!isMobileWidth()}
      fallback={
        <div class="h-full overflow-auto">
          {rankingEl}
          <Show when={selecionado()}>{(id) => <DetalheCliente id={id()} ano={ano()} status={props.status} />}</Show>
        </div>
      }
    >
      <Resize.Zone direction="horizontal" gutter={8} minSize={280}>
        <Resize.Panel id="ped-ranking" minSize={300} target={42}>
          {rankingEl}
        </Resize.Panel>
        <Resize.Panel id="ped-cliente" minSize={360}>
          <Show when={selecionado()} fallback={<p class="p-6 text-sm text-ink-subtle">Escolha um cliente.</p>}>
            {(id) => <DetalheCliente id={id()} ano={ano()} status={props.status} />}
          </Show>
        </Resize.Panel>
      </Resize.Zone>
    </Show>
  )
}

function DetalheCliente(props: { id: number; ano: number | null; status: StatusPedido[] | undefined }) {
  const [d] = createResource(
    () => ({ id: props.id, ano: props.ano, v: versaoDados() }),
    ({ id, ano }) => api.pedidos.compraCliente(id, { ano }),
  )
  const qtd = (n: number) => numero(n).replace(/,00$/, '')
  return (
    <div class="h-full overflow-auto p-5 flex flex-col gap-6">
      <Show when={d.latest} fallback={<p class="text-sm text-ink-subtle">Carregando…</p>}>
        {(c) => (
          <>
            <div>
              <h2 class="text-lg font-semibold text-ink">{c().cliente}</h2>
              <p class="text-sm text-ink-subtle">
                {moeda(c().totalValor)} · {inteiro(c().pedidos)} pedido(s){props.ano ? ` em ${props.ano}` : ''}
              </p>
            </div>

            <BarrasHorizontais
              titulo="Quantidade por produto"
              vazio="Os pedidos deste cliente não têm itens com produto."
              barras={c().porProduto.map((p) => ({
                rotulo: p.produto,
                valor: p.quantidade,
                texto: p.quantidade > 0 ? qtd(p.quantidade) : 'sem qtd.',
                dica: `${p.produto}: ${qtd(p.quantidade)} un. em ${p.pedidos} pedido(s)${p.ultimo ? ` · último ${dataBR(p.ultimo)}` : ''}`,
              }))}
            />

            <Show when={c().porAno.length > 1 || !props.ano}>
              <Colunas
                titulo="Valor por ano"
                barras={c().porAno.map((a) => ({
                  rotulo: String(a.ano),
                  valor: a.valor,
                  texto: moedaCurta(a.valor),
                  dica: `${a.ano}: ${moeda(a.valor)} · ${a.pedidos} pedido(s) · ${qtd(a.quantidade)} un.`,
                }))}
              />
            </Show>

            <section class="flex flex-col gap-2">
              <h3 class="text-xs font-medium uppercase tracking-wide text-ink-subtle">Pedidos</h3>
              <table class="w-full text-[13px]">
                <thead class="text-left text-ink-subtle">
                  <tr>
                    <th class="font-medium py-1 pr-2 w-5" aria-label="Status" />
                    <th class="font-medium py-1 pr-3">Ano</th>
                    <th class="font-medium py-1 pr-3">Pedido</th>
                    <th class="font-medium py-1 pr-3">Itens</th>
                    <th class="font-medium py-1 text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={c().linhas}>
                    {(l) => (
                      <tr class="border-t border-edge-muted align-top">
                        <td class="py-1.5 pr-2">
                          <span
                            class="block mt-0.5 size-2.5 rounded-full border border-black/25"
                            style={{ background: corDoStatus(props.status, l.statusId) ?? 'transparent' }}
                            title={props.status?.find((s) => s.id === l.statusId)?.nome}
                          />
                        </td>
                        <td class="py-1.5 pr-3 tabular-nums text-ink-muted">{l.ano}</td>
                        <td class="py-1.5 pr-3 text-ink whitespace-nowrap">{l.pedido ?? '—'}</td>
                        <td class="py-1.5 pr-3 text-ink-muted">{l.itens ?? '—'}</td>
                        <td class="py-1.5 text-right tabular-nums text-ink whitespace-nowrap">{l.valor !== null ? numero(l.valor) : (l.valorTexto ?? '—')}</td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </section>
          </>
        )}
      </Show>
    </div>
  )
}
